// safety/incidents-and-injuries/components/tabs/CertificatesTab.tsx
import React, { useState } from 'react';
import { TriangleAlert, Trash2 } from 'lucide-react';
import { SectionTitle, AddButton, FormModal } from '../shared';
import { fmtDate, capacityColor, daysUntil, todayISO } from '../../utils/incidents-utils';
import type { Incident, Certificate, SafetyLists } from '../../../../../types/incident.types';

export const CertificatesTab: React.FC<{
    incident: Incident;
    lists: SafetyLists;
    onAdd: (c: Certificate) => void;
    onDelete: (id: string) => void;
}> = ({ incident: r, lists, onAdd, onDelete }) => {
    const [modal, setModal] = useState(false);
    const expiring = r.certificates.find((c) => daysUntil(c.end) <= 4);

    return (
        <>
            {expiring && (
                <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 mb-5">
                    <div className="flex items-center gap-2 text-sm font-medium text-destructive">
                        <TriangleAlert size={16} />
                        Certificate Expiry Warning
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                        {expiring.id} expires{' '}
                        {daysUntil(expiring.end) < 0
                            ? `${Math.abs(daysUntil(expiring.end))} days ago`
                            : `in ${daysUntil(expiring.end)} days`}{' '}
                        ({fmtDate(expiring.end)}). Upload a new certificate to keep the RTW plan active.
                    </p>
                </div>
            )}

            <SectionTitle title="Certificates of Capacity" right={<AddButton label="Upload Certificate" onClick={() => setModal(true)} />} />
            <div className="rounded-lg border border-border overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-muted/30 border-b border-border">
                        <tr>
                            {['Certificate #', 'Start Date', 'End Date', 'Capacity Status', 'Doctor', 'Uploaded', ''].map((h) => (
                                <th key={h} className="text-left font-medium text-[11px] uppercase tracking-wide text-muted-foreground px-3 py-2.5">
                                    {h}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {r.certificates.length === 0 && (
                            <tr>
                                <td colSpan={7} className="px-3 py-8 text-center text-sm text-muted-foreground">
                                    No certificates yet.
                                </td>
                            </tr>
                        )}
                        {r.certificates.map((c) => (
                            <tr key={c.id} className="border-b border-border last:border-0">
                                <td className="px-3 py-3 text-primary font-medium">{c.id}</td>
                                <td className="px-3 py-3">{fmtDate(c.start)}</td>
                                <td className={`px-3 py-3 ${daysUntil(c.end) <= 4 ? 'text-destructive font-medium' : ''}`}>{fmtDate(c.end)}</td>
                                <td className="px-3 py-3">
                                    <span className={`${capacityColor(c.capacity)} font-medium`}>{c.capacity}</span>
                                </td>
                                <td className="px-3 py-3 text-muted-foreground">{c.doctor}</td>
                                <td className="px-3 py-3 text-muted-foreground">{fmtDate(c.uploaded)}</td>
                                <td className="px-3 py-3 text-right">
                                    <button onClick={() => onDelete(c.id)} className="text-muted-foreground hover:text-destructive">
                                        <Trash2 size={16} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {modal && (
                <FormModal
                    title="Upload Certificate"
                    fields={[
                        { key: 'id', label: 'Certificate #', value: `COC-${new Date().getFullYear()}-${Math.floor(Math.random() * 900 + 100)}` },
                        { key: 'start', label: 'Start Date', type: 'date', value: todayISO() },
                        { key: 'end', label: 'End Date', type: 'date', value: '' },
                        { key: 'capacity', label: 'Capacity Status', type: 'select', options: lists.capacityStatus, value: 'Light Duties' },
                        { key: 'doctor', label: 'Doctor', value: '' },
                    ]}
                    onClose={() => setModal(false)}
                    onSubmit={(v) => {
                        onAdd({ ...(v as any), uploaded: todayISO() });
                        setModal(false);
                    }}
                />
            )}
        </>
    );
};
